// ActionRisksTab — list of risks linked to the current action, with link/unlink.
// Data comes from GET /actions/{action_id}/risks.
// Actions own this relationship via action_links table.

import { useState, useMemo } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { actionsApi, type LinkedActionRisk } from '@/lib/api/actions'
import { risksApi, type Risk, lifecycleStateLabels, lifecycleStateColors } from '@/lib/api/risks'
import { registersApi } from '@/lib/api/registers'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { LinkPickerDialog, type PickerTreeNode } from '@/components/shared/LinkPickerDialog'
import { ConfirmUnlinkDialog } from '@/components/shared/ConfirmUnlinkDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertTriangle,
  FolderOpen,
  Calendar,
  User,
  ExternalLink,
  Plus,
  Unlink,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export function ActionRisksTab() {
  const { slug } = useTenant()
  const { actionId } = useParams({ strict: false }) as { actionId: string }
  const navigate = useNavigate()
  const api = useApi()
  const queryClient = useQueryClient()
  const actApi = actionsApi(api)

  const [pickerOpen, setPickerOpen] = useState(false)
  const [unlinkTarget, setUnlinkTarget] = useState<LinkedActionRisk | null>(null)

  // Linked risks for this action
  const {
    data: risks,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.actions.risks(slug, actionId),
    queryFn: () => actApi.risks(actionId),
  })

  // Register tree for the picker — fetched when picker opens
  const { data: registerTree, isLoading: registersLoading } = useQuery({
    queryKey: queryKeys.registers.tree(slug),
    queryFn: () => registersApi(api).tree(),
    enabled: pickerOpen,
  })

  // All risks across all registers — fetched by loading each register's risks
  // The register tree gives us accessible register IDs; each register's list is permission-filtered
  const { data: allRisks, isLoading: risksLoading } = useQuery({
    queryKey: [...queryKeys.risks.all(slug), 'picker-all'],
    queryFn: async () => {
      if (!registerTree || registerTree.length === 0) return []
      const rApi = risksApi(api)
      const registerIds = registerTree.map((r) => r.id)
      const results = await Promise.all(registerIds.map((id) => rApi.list(id)))
      return results.flat()
    },
    enabled: pickerOpen && !!registerTree,
  })

  const existingIds = useMemo(
    () => new Set(risks?.map((r) => r.risk_id) ?? []),
    [risks],
  )

  // Unlink mutation
  const unlinkMutation = useMutation({
    mutationFn: ({ linkId, notes }: { linkId: string; notes: string }) =>
      actApi.unlinkRisk(actionId, linkId, notes || undefined),
    onSuccess: () => {
      setUnlinkTarget(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.actions.risks(slug, actionId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.actions.detail(slug, actionId) })
    },
  })

  if (isLoading) return <TableSkeleton rows={4} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load linked risks"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header with count and add button */}
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {risks?.length ?? 0} linked risk{risks?.length !== 1 ? 's' : ''}
        </div>
        <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          Link Risk
        </Button>
      </div>

      {/* Empty state */}
      {(!risks || risks.length === 0) && (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          <div className="space-y-2 text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm">No linked risks.</p>
            <p className="text-xs text-muted-foreground/60">
              Use the button above to link risks to this action.
            </p>
          </div>
        </div>
      )}

      {/* Risk list */}
      {risks && risks.length > 0 && (
        <div className="divide-y rounded-lg border">
          {risks.map((risk) => (
            <RiskRow
              key={risk.link_id}
              risk={risk}
              onClick={() => navigate({ to: `/t/${slug}/risks/${risk.risk_id}` })}
              onUnlink={() => setUnlinkTarget(risk)}
            />
          ))}
        </div>
      )}

      {/* Link picker dialog */}
      <LinkPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onLinked={() => {
          setPickerOpen(false)
          queryClient.invalidateQueries({ queryKey: queryKeys.actions.risks(slug, actionId) })
          queryClient.invalidateQueries({ queryKey: queryKeys.actions.detail(slug, actionId) })
        }}
        title="Link Risk"
        description="Select a risk to link to this action."
        treeNodes={registerTree as PickerTreeNode[] | undefined}
        objects={allRisks}
        isLoading={risksLoading || registersLoading}
        existingIds={existingIds}
        getObjectId={(r: Risk) => r.id}
        getObjectParentId={(r: Risk) => r.register_id}
        getObjectSearchText={(r: Risk) => `${r.name} ${r.taxonomy_node_name ?? ''}`}
        renderObject={(r: Risk, isSelected: boolean) => (
          <RiskPickerRow risk={r} isSelected={isSelected} />
        )}
        linkFn={(riskId: string) => actApi.linkRisk(actionId, riskId)}
        linkButtonLabel="Link Risk"
        searchPlaceholder="Search risks or registers..."
      />

      {/* Unlink confirmation dialog */}
      {unlinkTarget && (
        <ConfirmUnlinkDialog
          open={!!unlinkTarget}
          onClose={() => setUnlinkTarget(null)}
          onConfirm={(notes) =>
            unlinkMutation.mutate({ linkId: unlinkTarget.link_id, notes })
          }
          title="Remove Risk Link"
          description={`Remove the link between this action and "${unlinkTarget.risk_name}"?`}
          isPending={unlinkMutation.isPending}
        />
      )}
    </div>
  )
}

function RiskRow({
  risk,
  onClick,
  onUnlink,
}: {
  risk: LinkedActionRisk
  onClick: () => void
  onUnlink: () => void
}) {
  return (
    <div
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-accent/30"
      onClick={onClick}
    >
      {/* Name */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{risk.risk_name}</span>
          <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
        </div>
      </div>

      {/* Register */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground md:flex">
        <FolderOpen className="h-3 w-3" />
        <span className="max-w-[120px] truncate">{risk.register_name}</span>
      </div>

      {/* Owner */}
      {risk.owner_display_name && (
        <div className="hidden items-center gap-1 text-xs text-muted-foreground lg:flex">
          <User className="h-3 w-3" />
          <span className="max-w-[100px] truncate">{risk.owner_display_name}</span>
        </div>
      )}

      {/* Linked date */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground xl:flex">
        <Calendar className="h-3 w-3" />
        <span>{new Date(risk.linked_at).toLocaleDateString()}</span>
      </div>

      {/* Status badge */}
      <Badge
        variant="outline"
        className={cn(
          'flex-shrink-0 px-1.5 py-0 text-xs',
          lifecycleStateColors[risk.lifecycle_state] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {lifecycleStateLabels[risk.lifecycle_state] ?? risk.lifecycle_state}
      </Badge>

      {/* Unlink button */}
      <button
        className="flex-shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
        onClick={(e) => {
          e.stopPropagation()
          onUnlink()
        }}
        title="Remove link"
      >
        <Unlink className="h-4 w-4" />
      </button>
    </div>
  )
}

function RiskPickerRow({ risk, isSelected }: { risk: Risk; isSelected: boolean }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={cn('truncate', isSelected && 'font-medium')}>{risk.name}</span>
      <Badge
        variant="outline"
        className={cn(
          'ml-auto flex-shrink-0 px-1.5 py-0 text-xs',
          lifecycleStateColors[risk.lifecycle_state] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {lifecycleStateLabels[risk.lifecycle_state] ?? risk.lifecycle_state}
      </Badge>
    </div>
  )
}
